import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, X } from 'lucide-react';
import type { SongSummary } from '../../types';

interface RankableItemProps {
  song: SongSummary;
  rank: number;
  points: number;
  onRemove: (songId: number) => void;
}

export function RankableItem({ song, rank, points, onRemove }: RankableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: song.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-3 bg-monster-dark border border-monster-border rounded hover:border-monster-blue transition-colors"
    >
      {/* Drag Handle */}
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-white transition-colors"
        aria-label="Drag to reorder"
      >
        <GripVertical size={20} />
      </button>

      {/* Rank Badge */}
      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-monster-blue flex items-center justify-center font-bold text-sm">
        #{rank}
      </div>

      {/* Song Thumbnail */}
      <img
        src={song.imagePath}
        alt={song.title}
        className="w-12 h-12 rounded object-cover"
      />

      {/* Song Info */}
      <div className="flex-1 min-w-0">
        <h4 className="font-medium truncate">{song.title}</h4>
        <p className="text-sm text-gray-400 truncate">{song.artist}</p>
      </div>

      {/* Points Badge */}
      <div className="flex-shrink-0 px-3 py-1 bg-monster-card rounded-full text-sm font-medium text-monster-blue">
        {points} pts
      </div>

      {/* Remove Button */}
      <button
        onClick={() => onRemove(song.id)}
        className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full hover:bg-red-600/20 text-gray-400 hover:text-red-500 transition-colors"
        aria-label="Remove from vote"
      >
        <X size={18} />
      </button>
    </div>
  );
}
